import React, { useEffect, useState } from "react";
import { getOAuthHeaders } from "../../constants/oAuthValidation";
import { posOrderProcessor1, salesHistoryAggregator, upsertOfdOrderStatus } from "../../services/index";
import { getSalesHistoryDetails } from "../../utility/salesHistoryUtils";
import PrintController from "../../lib/printer/printController";
import CWKOTPrinter from "../../lib/printer/CWKotPrinter";
import { useHistory } from "react-router-dom";
import { message } from "antd";
import db from "../../database";
import Header from "./Header";
import OrderList from "./OrderList";
import OrderDetails from "./OrderDetails";
import ActionButtons from "./ActionButtons";
import messages from "../../errors/messages.en-IN.json";

const Aggregator = ({ orderUpdateFlag }) => {
  const history = useHistory();
  const [currentDateTime, setCurrentDateTime] = useState(new Date());
  const [orders, setOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [selectedOrderToPrint, setSelectedOrderToPrint] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedChannel, setSelectedChannel] = useState("All Channels");
  const [searchValue, setSearchValue] = useState("");
  const [filteredOrdersState, setFilteredOrdersState] = useState([]);
  const [isStatusUpdating, setIsStatusUpdating] = useState(false);
  const authHeaders = getOAuthHeaders();
  const tillValue = JSON.parse(localStorage.getItem("tillValue"));
  const tillSession = JSON.parse(localStorage.getItem("tillSession"));
  const disableAggregatorInvoicePrint = process.env.REACT_APP_DISABLE_AGGREGATOR_INVOICE_PRINT === "true";
  const disableAggregatorKOTPrint = process.env.REACT_APP_DISABLE_AGGREGATOR_KOT_PRINT === "true";

  const statusDisplayMapping = {
    NEW: "New",
    AK: "Acknowledged",
    FR: "Food Ready",
    DI: "Dispatched",
    CC: "Cancelled",
  };

  useEffect(() => {
    const interval = setInterval(() => setCurrentDateTime(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentDateTime(new Date());
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    let isMounted = true;
    let timeoutId = null;

    const fetchDataWithInterval = async () => {
      if (!isMounted) return;

      await getSalesHistoryData();

      // Schedule the next fetch only after the current one completes
      timeoutId = setTimeout(fetchDataWithInterval, 30000); // 30 seconds
    };

    // Initial fetch
    fetchDataWithInterval();

    // Cleanup on unmount
    return () => {
      isMounted = false;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, []);

  useEffect(() => {
    if (searchValue) {
      const filtered = orders.filter((order) => order.orderNo.toLowerCase().includes(searchValue.toLowerCase()));
      setFilteredOrdersState(filtered);
    } else {
      setFilteredOrdersState(orders);
    }
  }, [searchValue, orders]);

  const getSalesHistoryData = async () => {
    const sortOptions = { documentno: "desc", dateordered: "desc", customer: null, totalAmount: null };
    const filteredSortOptions = Object.fromEntries(Object.entries(sortOptions).filter(([_, value]) => value !== null));
    const sortData = JSON.stringify(filteredSortOptions).replace(/"/g, '\\"');
    setLoading(true);
    try {
      const response = await salesHistoryAggregator(sortData, tillValue);
      const salesDataArray = response?.data?.data?.salesHistory || [];
      if (salesDataArray.length > 0) {
        const newOrders = salesDataArray
          .filter((salesData) => ["NEW", "AK", "FR", "DI"].includes(salesData.ofdStatus))
          .map((salesData) => ({
            sOrderID: salesData.sOrderID,
            orderNo: salesData.documentno,
            orderTime: salesData.orderTime,
            channel: salesData.saleType?.name || "Unknown",
            status: salesData.ofdStatus || "NEW",
            amount: salesData.grosstotal,
            customer: salesData?.cwrB2cCustomer?.name || "",
          }));
        setOrders(newOrders);
        setFilteredOrdersState(newOrders);
        if (!selectedOrder && newOrders.length > 0) {
          const orderDetails = await getSalesHistoryDetails(newOrders[0].sOrderID, newOrders[0].status, false);
          if (orderDetails) setSelectedOrder(orderDetails);
        } else if (selectedOrder && !isStatusUpdating) {
          const updatedOrder = newOrders.find((order) => order.sOrderID === selectedOrder.sOrderID);
          if (updatedOrder && updatedOrder.status !== selectedOrder.status) {
            setSelectedOrder((prev) => ({ ...prev, status: updatedOrder.status }));
          }
        }
      }
    } catch (error) {
      console.error("Error fetching order history:", error);
      message.error(`${messages.errors.orders.status_fetch_error}`);
    } finally {
      setLoading(false);
    }
  };

  const updateOrderStatus = async (sOrderID, newStatus, isAuto = false, reasonCode = null) => {
    const statusMapping = { Acknowledged: "AK", Cancelled: "CC", "Food Ready": "FR", Dispatched: "DI" };
    const statusCode = statusMapping[newStatus] || newStatus;
    setLoading(true);
    setIsStatusUpdating(true);
    try {
      const response = await upsertOfdOrderStatus(sOrderID, statusCode, null, reasonCode, "N");
      const result = response?.data?.data?.upsertOfdOrderStatus;
      if (result?.status === "200") {
        message.success(isAuto ? "Order automatically accepted after timeout!" : result?.message);
        await getSalesHistoryData();
        if (selectedOrder?.sOrderID === sOrderID) {
          setSelectedOrder((prev) => ({ ...prev, status: statusCode }));
          if (statusCode !== "CC") {
            const orderDetails = await getSalesHistoryDetails(sOrderID, statusCode, statusCode === "AK");
            if (orderDetails) setSelectedOrder(orderDetails);
          }
        }
        if (statusCode === "CC" && selectedOrder?.sOrderID === sOrderID) {
          setSelectedOrder(null);
        }
      } else {
        message.error(`${messages.errors.orders.status_update_failed}`);
      }
    } catch (error) {
      console.error("Error updating order status:", error);
      message.error(`${messages.errors.orders.status_update_error}`);
    } finally {
      setLoading(false);
      setIsStatusUpdating(false);
    }
  };

  const syncOrder = async (order) => {
    const tillData = JSON.parse(localStorage.getItem("tillData"));
    let storeOpsTillID;
    const tillEvent = await db.tillEvents.where("tillSessionId").equals(tillSession.tillSessionId).first();
    if (tillEvent) storeOpsTillID = tillEvent.storeDailyOpsTillid;
    const orderJson = {
      sOrderID: order.sOrderID,
      cSClientID: tillData.tillAccess.csClientId,
      cSBunitID: tillData.tillAccess.csBunit.csBunitId,
      created: order.orderTime,
      createdby: order.createdBy,
      updated: order.orderTime,
      updatedby: order.createdBy,
      csDoctypeId: tillData.tillAccess.csBunit.cwrCsDoctypeId,
      sCustomerId: tillData.tillAccess.csBunit.b2cCustomer.sCustomer.sCustomerID,
      sCustomerBillingId: tillData.tillAccess.csBunit.customerAddress?.sCustomerAddressID,
      sCustomerShippingId: tillData.tillAccess.csBunit.customerAddress?.sCustomerAddressID,
      sPricelistId: tillData.tillAccess.csBunit.cwrSpricelistId,
      documentno: order.orderNo,
      dateordered: order.orderTime,
      datepromised: order.orderTime,
      csPaymenttermID: null,
      finPaymentmethodId: order.paymentMode || null,
      csCurrencyId: tillData?.tillAccess?.csBunit?.currencies[0]?.csCurrencyId || "",
      mWarehouseId: tillData.tillAccess.csBunit.mWarehouse.mWarehouseID,
      cwrLongitude: "",
      cwrLatitude: "",
      csUserId: tillData.tillAccess.csUserId,
      cwrB2cCustomerId: order.customer?.cwrCustomerId || order.customer?.b2cCustomerId,
      orderreference: "",
      cwrPayref: "",
      cwrPayremarks: "",
      description: order?.description || "",
      storeDailyOpsTillid: storeOpsTillID,
      cwrTillId: tillData.tillAccess.cwrTill.cwrTillID,
      redemption: 0,
      accumulation: 0,
      redeemRefId: null,
      roundoff: parseFloat(order?.roundOff?.toFixed(2)) || 0,
      cwrProductQty: order?.totalQty || 0,
      cwrProductCount: order?.totalQty || 0,
      ofdStatus: "Dispatched",
      ofdIspaid: "Y",
      posLoyaltyCalculation: JSON.parse(localStorage.getItem("posConfig"))?.disablePOSLoyaltyCalculation,
      mPricingruleId: order?.mPricingruleId ?? null,
      cwrSaletypeId: order.channel === "Swiggy" ? "SWIGGY" : order.channel === "Zomato" ? "ZOMATO" : "",
      salesRepId: order?.salesRepId || null,
      discAmount: parseFloat(order.discount?.toFixed(2)) || 0,
      creditAmount: order?.creditAmount,
      metaData: {},
      giftCard: [],
      pricingCoupon: [{ mPricingCouponId: null, redemptionCount: null, referenceId: null, couponCode: null }],
      orderTotal: parseFloat(order.amount?.toFixed(2)) || 0,
      nettotal: parseFloat((order.amount - order.tax)?.toFixed(2)) || 0,
      taxamt: parseFloat(order.tax?.toFixed(2)) || 0,
      isReturn: "N",
      sOrderReturnId: order?.items?.[0]?.sOrderReturnId || null,
      layAway: order.layAway || null,
      payments: order.paymentMode ? [{ paymentMethod: order.paymentMode, amount: order.amount }] : [],
      tax: order.tax ? [{ amount: order.tax }] : [],
      line:
        order.items?.map((item) => ({
          productName: item.name,
          qty: item.quantity,
          price: item.price,
          discount: item.discount,
          tax: item.tax,
        })) || [],
    };
    const stringifiedData = JSON.stringify(orderJson);
    const newStringRequest = JSON.stringify(stringifiedData.replace(/\\"/g, '\\"'));
    const paramsInput = {
      query: `
        mutation {
          posOrderProcessor1(
            tillId: "${tillData.tillAccess.cwrTill.cwrTillID}",
            orderId: "${order.sOrderID}",
            documentNo: "${order.orderNo}",
            orderJson: ${newStringRequest}
          ) {
            documentno
            status
            message
          }
        }
      `,
    };
    try {
      const response = await posOrderProcessor1(paramsInput);
      const result = response?.data?.data?.posOrderProcessor1;
      if (result?.status === "200") {
        message.success(`Order ${result.documentno} synced successfully!`);
      } else {
        message.error(`${messages.errors.orders.status_sync_failed}`);
      }
    } catch (error) {
      console.error("Error syncing order:", error);
      message.error(`${messages.errors.orders.status_sync_error}`);
    }
  };

  const handleOrderClick = (order) => {
    getSalesHistoryDetails(order.sOrderID, order.status, false, null, true).then((data) => {
      if (data) {
        const { detailedOrder, transformedOrder } = data;
        if (detailedOrder) {
          setSelectedOrder(detailedOrder);
          setSelectedOrderToPrint(transformedOrder);
        }
      }
    });
  };

  const handlePrint = () => {
    if (selectedOrder) {
      try {
        if (!disableAggregatorInvoicePrint) {
          PrintController(selectedOrderToPrint);
        } else if (!disableAggregatorKOTPrint) {
          CWKOTPrinter(selectedOrderToPrint, true);
        }
      } catch (error) {
        console.error("Error printing order:", error);
        message.error(`${messages.errors.orders.status_print_failed}`);
      }
    }
  };

  const rejectReasons = [
    "item_out_of_stock",
    "store_closed",
    "store_busy",
    "rider_not_available",
    "out_of_delivery_radius",
    "connectivity_issue",
    "total_missmatch",
    "invalid_item",
    "option_out_of_stock",
    "invalid_option",
    "unspecified",
  ];

  return (
    <div style={{ height: "100%", backgroundColor: "#f3f4f9", padding: "8px" }}>
      <Header currentDateTime={currentDateTime} tillValue={tillValue} onBack={() => history.push("/pos")} onPosClick={() => history.push("/pos")} />
      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: "12px" }}>
        <OrderList
          orders={filteredOrdersState} // Pass filteredOrdersState instead of orders
          selectedChannel={selectedChannel}
          setSelectedChannel={setSelectedChannel}
          searchValue={searchValue}
          setSearchValue={setSearchValue}
          loading={loading}
          onOrderClick={handleOrderClick}
          onRefresh={getSalesHistoryData}
          statusDisplayMapping={statusDisplayMapping}
        />
        <OrderDetails
          selectedOrder={selectedOrder}
          statusDisplayMapping={statusDisplayMapping}
          onPrint={handlePrint}
          actionButtons={
            <ActionButtons
              selectedOrder={selectedOrder}
              loading={loading}
              onAccept={() => updateOrderStatus(selectedOrder?.sOrderID, "Acknowledged")}
              onReject={(reason) => updateOrderStatus(selectedOrder?.sOrderID, "Cancelled", false, reason)}
              onMarkAsReady={() => updateOrderStatus(selectedOrder?.sOrderID, "Food Ready")}
              onComplete={() => {
                updateOrderStatus(selectedOrder?.sOrderID, "Dispatched").then(() => syncOrder(selectedOrder));
              }}
              rejectReasons={rejectReasons}
            />
          }
        />
      </div>
    </div>
  );
};

export default Aggregator;
